// Display formats shared by the S1-16 screens. Unit conventions match the Friday report
// (services/api/src/reports/friday/format.ts), so a number reads the same on screen, in the
// email and in the PDF: percent = fraction (×100 to show), currency = minor units, time =
// minutes, score 0–100+, attainment a fraction. Missing values read "Awaiting data", never 0.
import type { ScorecardMetric } from "../api/types";

export const AWAITING_DATA = "Awaiting data";

const DIRECTION_SYMBOL: Record<ScorecardMetric["direction"], string> = {
  gte: "≥",
  gt: ">",
  lte: "≤",
  lt: "<",
  eq: "=",
  between: "↔",
};

export function fractionDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function formatMoney(
  minor: number,
  currency: string,
  opts: { whole?: boolean } = {},
): string {
  const digits = fractionDigits(currency);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    ...(opts.whole ? { maximumFractionDigits: 0, minimumFractionDigits: 0 } : {}),
  }).format(minor / 10 ** digits);
}

/** Minutes → "7 h 05 min" (or "45 min"). */
export function formatMinutes(minutes: number): string {
  const m = Math.round(minutes);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest} min`;
  return rest === 0 ? `${h} h` : `${h} h ${String(rest).padStart(2, "0")} min`;
}

/** Minutes → hours with one decimal ("7.1 h"), for dense tables. */
export function formatHours(minutes: number): string {
  return `${(Math.round((minutes / 60) * 10) / 10).toLocaleString("en-US")} h`;
}

/**
 * A measurable's number on a dense card (the chart's cards and seat panel): time as decimal
 * hours ("30.5 h", not 1,830 minutes), a percent as "93%", any other unit as a plain number.
 */
export function formatCardMetric(v: number | null, unit: ScorecardMetric["unit"]): string {
  if (v === null) return "—";
  if (unit === "time") return formatHours(v);
  if (unit === "percent") return `${Math.round(v * 100)}%`;
  return Number.isInteger(v) ? v.toLocaleString("en-US") : v.toFixed(1);
}

function formatNumber(v: number): string {
  return Number.isInteger(v)
    ? v.toLocaleString("en-US")
    : (Math.round(v * 10) / 10).toLocaleString("en-US");
}

export function formatMetricValue(
  v: number | null,
  unit: ScorecardMetric["unit"],
  currency: string,
): string {
  if (v === null) return AWAITING_DATA;
  switch (unit) {
    case "currency":
      return formatMoney(v, currency);
    case "percent":
      return `${Math.round(v * 100)}%`;
    case "time":
      return formatMinutes(v);
    case "yes_no":
      return v > 0 ? "Yes" : "No";
    case "done_not_done":
      return v > 0 ? "Done" : "Not done";
    default:
      return formatNumber(v);
  }
}

export function formatTarget(m: ScorecardMetric, currency: string): string {
  if (m.target === null) return "—";
  const f = (x: number) => formatMetricValue(x, m.unit, currency);
  if (m.direction === "between" && m.target_max !== null)
    return `${f(m.target)} – ${f(m.target_max)}`;
  return `${DIRECTION_SYMBOL[m.direction]} ${f(m.target)}`;
}

export function formatScore(score: number | null): string {
  return score === null ? AWAITING_DATA : `${Math.round(score)}/100`;
}

export function formatAttainment(a: number | null): string {
  return a === null ? "—" : `${Math.round(a * 100)}%`;
}

export function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** A recent moment, short: the time when it is today ("5:24 AM"), else the date ("Oct 21"). */
export function formatRecent(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const today =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return today
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatDate(iso: string): string {
  return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "candidate.sourced" → "Candidate sourced". */
export function humanize(dotted: string): string {
  const s = dotted.replace(/[._]/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const COST_KIND_LABELS: Record<string, string> = {
  de_subscription: "Subscription",
  de_usage: "Usage",
  agent_tokens: "Agent tokens",
  agent_compute: "Agent compute",
  agent_tool: "Agent tools",
  human_time: "Human time",
  adjustment: "Adjustment",
  reversal: "Reversal",
};

export const TIME_KIND_LABELS: Record<string, string> = {
  work: "Work",
  supervision: "Supervision",
  founder: "Founder",
  setup: "Setup",
  other: "Other",
};

export const REPORT_STATE_LABELS: Record<string, string> = {
  draft: "Draft",
  generated: "Generated",
  sent: "Sent",
  failed: "Failed",
};

export const TRACKING_LABELS: Record<ScorecardMetric["tracking_mode"], string> = {
  scored: "KPI",
  context: "Context",
  off: "Off",
};

export const STATE_LABELS: Record<ScorecardMetric["state"], string> = {
  complete: "Complete",
  provisional: "Provisional",
  missing: "Missing",
  context: "Context",
  not_applicable: "Not applicable",
  restricted: "Restricted",
  unmeasured: "Unmeasured",
};

/** "Senior Recruiter · Priya Shah": a seat and who holds it, as one line. A vacant seat (no holder) is just its title. */
export function seatHolderLabel(seatTitle: string, holderName?: string | null): string {
  return holderName && holderName !== seatTitle ? `${seatTitle} · ${holderName}` : seatTitle;
}
